/**
 * Initiative Intake API
 *
 * Thin client over the FLOW-544 intake routes. Each mutating call runs an
 * Anthropic round trip on the backend, so they share the chat timeout rather
 * than the default fetch behaviour.
 */

import { api, ApiError } from "./client";
import type {
  InitiativeIntake,
  IntakeId,
  ProjectId,
  WorkItemId,
} from "./types";

/** Matches the chat client: the backend's Anthropic timeout plus headroom */
const INTAKE_TIMEOUT_MS = 130_000;

interface IntakesListResponse {
  intakes: InitiativeIntake[];
}

/** A turn returns the updated intake alongside the reply to show the user */
export interface IntakeTurnResponse {
  intake: InitiativeIntake;
  reply: string;
}

/** Run a request under the intake timeout, converting an abort into a 408. */
async function withTimeout<T>(
  run: (signal: AbortSignal) => Promise<T>,
): Promise<T> {
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), INTAKE_TIMEOUT_MS);

  try {
    return await run(controller.signal);
  } catch (err) {
    if (err instanceof DOMException && err.name === "AbortError") {
      throw new ApiError(408, "The planning request timed out. Please retry.");
    }
    throw err;
  } finally {
    clearTimeout(timeout);
  }
}

/**
 * Start an intake from a plain-language initiative description.
 *
 * `targetEpicId` pins the session to an existing epic (FLOW-615): the
 * breakdown attaches its stories to that epic instead of proposing one. It is
 * omitted from the body when absent so the server sees no key at all rather
 * than an explicit null.
 */
export async function startIntake(
  projectId: ProjectId,
  initiativeText: string,
  targetEpicId?: WorkItemId,
): Promise<InitiativeIntake> {
  return withTimeout((signal) =>
    api.post<InitiativeIntake>(
      `/projects/${projectId}/intakes`,
      {
        initiative_text: initiativeText,
        ...(targetEpicId ? { target_epic_id: targetEpicId } : {}),
      },
      { signal },
    ),
  );
}

/** List a project's intake sessions, newest first */
export async function listIntakes(
  projectId: ProjectId,
): Promise<InitiativeIntake[]> {
  const response = await api.get<IntakesListResponse>(
    `/projects/${projectId}/intakes`,
  );
  return response.intakes;
}

/** Fetch a single intake */
export async function getIntake(id: IntakeId): Promise<InitiativeIntake> {
  return api.get<InitiativeIntake>(`/intakes/${id}`);
}

/** Answer the current clarifying question */
export async function sendIntakeTurn(
  id: IntakeId,
  content: string,
): Promise<IntakeTurnResponse> {
  return withTimeout((signal) =>
    api.post<IntakeTurnResponse>(
      `/intakes/${id}/messages`,
      { content },
      { signal },
    ),
  );
}

/** Abandon the remaining questions and assemble the spec with best-judgment defaults */
export async function skipIntakeClarification(
  id: IntakeId,
): Promise<InitiativeIntake> {
  return withTimeout((signal) =>
    api.post<InitiativeIntake>(`/intakes/${id}/skip`, undefined, { signal }),
  );
}
